import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, Lock, Mail, Truck } from "lucide-react";

const BRANCH_EMAILS = [
  "berkshire@homego-removals.com",
  "buckinghamshire@homego-removals.com",
  "hampshire@homego-removals.com",
  "oxfordshire@homego-removals.com",
  "surrey@homego-removals.com",
  "london@homego-removals.com",
  "office@homego-removals.com",
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${user.branch_name}!`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#D4AF37] mb-4">
            <Truck className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-3xl font-bold text-white">HomeGo <span className="text-[#D4AF37]">Removals</span></h1>
          <p className="text-gray-400 mt-2 text-sm">Branch Sales Portal — Email Builder & Pro Manager</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-gray-300 text-sm flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#D4AF37]" /> Branch Email
              </Label>
              <Input
                type="email"
                data-testid="login-email-input"
                list="branch-emails"
                placeholder="london@homego-removals.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-11"
              />
              <datalist id="branch-emails">
                {BRANCH_EMAILS.map((b) => <option key={b} value={b} />)}
              </datalist>
            </div>

            <div>
              <Label className="text-gray-300 text-sm flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-[#D4AF37]" /> Password
              </Label>
              <Input
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-11"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#D4AF37] hover:bg-[#c9a532] text-black font-bold text-base"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-800">
            <p className="text-xs text-gray-500 flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" />
              Branches: Berkshire, Buckinghamshire, Hampshire, Oxfordshire, Surrey, London + Head Office
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
